import { useContextSelector } from 'use-context-selector';
import { usePrevious } from '@frameio/vapor';
import { AppStateContext } from './AppStateContext';

/** provides a dispatch function to update AppState */
export const useDispatchAppState = () =>
  useContextSelector(AppStateContext, (value) => value.dispatch);

/** provides the `commentScroll` AppState status */
export const useCommentScroll = () =>
  useContextSelector(AppStateContext, (value) => value.commentScroll);

/** provides the `assetClickBehavior` AppState status */
export const useAssetClickBehavior = () =>
  useContextSelector(AppStateContext, (value) => value.assetClickBehavior);

/** provides the workspaceId to scroll to, used by mobile nav & back to workspaces */
export const useScrollToWorkspaceId = () =>
  useContextSelector(AppStateContext, (value) => value.scrollToWorkspaceId);

/** provides the projectId to scroll to, used by mobile nav */
export const useScrollToNewProjectInWorkspaceId = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.scrollToNewProjectWorkspaceId
  );

/** provides the `moveToCopyToStatus` AppState status */
export const useMoveToCopyToStatus = () =>
  useContextSelector(AppStateContext, (value) => value.moveToCopyToStatus);

/** provides the `arePinsVisible` AppState status of `pointAndClickStatus` */
export const useArePinsVisible = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.pointAndClickStatus.arePinsVisible
  );

/** provides the `areSuggestionsVisible` AppState status of `viewSuggestionsStatus` */
export const useAreSuggestionsVisible = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.viewSuggestionsStatus.areSuggestionsVisible
  );

/** provides the `isSuggestionModeEnabled` AppState status of `suggestionModeStatus` */
export const useIsSuggestionModeEnabled = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.suggestionModeStatus.isSuggestionModeEnabled
  );

/** provides the `isSuggestionModeAvailable` AppState status of `suggestionModeStatus`, returns `false` if current page does not contain text` */
export const useIsSuggestionModeAvailable = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.suggestionModeStatus.isSuggestionModeAvailable
  );

/** provides the `isEditSuggestionModeEnabled` AppState status of `suggestionModeStatus` */
export const useIsEditSuggestionModeEnabled = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.suggestionModeStatus.isEditSuggestionModeEnabled
  );

/** provides the AppState status of anchor comment mode based on the value of `assetClickBehavior` */
export const useIsAnchoredCommentModeEnabled = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.assetClickBehavior === 'COMMENT'
  );

/** provides the `isCreatedCommentHiddenByFilters` AppState status */
export const useIsCreatedCommentHiddenByFilters = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.commentStatus.isCreatedCommentHiddenByFilters
  );

/** provides the `isViewAllAnnotationsEnabled` AppState status of `viewAllAnnotationsStatus` */
export const useIsViewAllAnnotationsEnabled = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.viewAllAnnotationsStatus.isViewAllAnnotationsEnabled
  );
/** provides the `areAnnotationsVisible` AppState status */
export const useAreAnnotationsVisible = () =>
  useContextSelector(AppStateContext, (value) => value.areAnnotationsVisible);

/** defines the type of comment where an action may have been triggered */
export const useCommentContainerTriggerType = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.commentContainerTriggerType
  );

/** provides the `ignoreEditorInitializationMode` AppState status */
export const useIgnoreEditorInitializationMode = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.ignoreEditorInitializationMode
  );

/** provides the `isMobileMenu` AppState status */
export const useIsMobileNavOpen = () =>
  useContextSelector(AppStateContext, (value) => value.isMobileNavOpen);

export const useIsMobileNavOpening = () => {
  const isMobileNavOpen = useIsMobileNavOpen();
  const previousIsMobileNavOpen = usePrevious(isMobileNavOpen);
  return isMobileNavOpen && !previousIsMobileNavOpen;
};

export const useIsMobileNavClosing = () => {
  const isMobileNavOpen = useIsMobileNavOpen();
  const previousIsMobileNavOpen = usePrevious(isMobileNavOpen);
  return !isMobileNavOpen && previousIsMobileNavOpen;
};

/** provides the `shareAppearance` AppState values */
export const useShareAppearance = () =>
  useContextSelector(AppStateContext, (value) => value.shareAppearance);

/** provides the `accessRevoked` AppState values */
export const useAccessRevoked = () =>
  useContextSelector(AppStateContext, (value) => value.accessRevoked);

/** provides the `selectedAssetType` AppState values */
export const useSelectedAssetType = () =>
  useContextSelector(AppStateContext, (value) => value.selectedAssetType);

/**
 * get the `FieldValueValues` and the id of the fields currently being edited.
 * this allows synced realtime editing across multiple AssetField components
 * anywhere they exist in the app
 * */
export const useEditingAssetFields = (fieldValueId: string) =>
  useContextSelector(AppStateContext, (value) => {
    if (
      fieldValueId &&
      value.currentlyEditingAssetField.fieldValueIds?.includes(fieldValueId)
    )
      return value.currentlyEditingAssetField;
    return null;
  });

export const useIsInProjectSearchOpen = () =>
  useContextSelector(AppStateContext, (value) => {
    return value.isInProjectSearchOpen;
  });

/** provides the `hasCompletedMigrationFlowInThisSession` AppState value, used in account migration flow */
export const useHasCompletedMigrationFlowInThisSession = () =>
  useContextSelector(
    AppStateContext,
    (value) => value.hasCompletedMigrationFlowInThisSession
  );

/** provides the `panelVisibilityHistory` AppState values */
export const usePanelVisibilityHistory = () =>
  useContextSelector(AppStateContext, (value) => value.panelVisibilityHistory);

export const useIsFullscreenActive = () =>
  useContextSelector(AppStateContext, (value) => value.isFullscreenActive);
